import {Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {FoldDetailService} from '../../../../services/apiServices/business/fold-detail.service';
import {TranslateService} from '@ngx-translate/core';
import {BROWSERS, DeviceDetectorService, DeviceInfo} from 'ngx-device-detector';
import {ApiConst} from '../../../../services/apiServices/api-const';
import {HttpService} from '../../../../services/apiServices/http-service.service';
import {FileSaverService} from 'ngx-filesaver';
import {map, switchMap} from "rxjs/operators";
import {from} from "rxjs";

/**
 * Customizable list for at least 2 use Case: Treatments/Deposits of a fold
 */
@Component({
  selector: 'app-custom-list',
  templateUrl: './custom-list.component.html',
  styleUrls: ['./custom-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CustomListComponent implements OnInit {

  /**
   * list of colums for the p-table
   */
  @Input('columns') public columns;

  /**
   * datas for the p-table
   */
  @Input('datas') public datas;

  /**
   * if each rows can change of index
   */
  @Input('movable') public movable: boolean;

  /**
   * if the content of a row can be consultable (modal pdf)
   */
  @Input('consultable') public consultable: boolean;

  /**
   * if the content of a row can be downloaded
   */
  @Input('downloadable') public downloadable: boolean;

  /**
   * if the content of a row can be supressed
   */
  @Input('removable') public removable: boolean;

  /**
   * if the content of a row can be downloaded as a TXT file
   */
  @Input('convertibleAsText') public convertibleAsText: boolean;

  private deviceInfo: DeviceInfo;

  /**
   * whether to display consult modal or not
   */
  public displayConsult: boolean;

  /**
   * fileName to display as modal title
   */
  public displayConsultName: string;

  /**
   * If we need to display left arrow during displayFiles (navigation purposes)
   */
  public displayLeftArrow: boolean;

  /**
   * If we need to display right arrow during displayFiles (navigation purposes)
   */
  public displayRightArrow: boolean;

  /**
   * the index of the p-table that is currently active (usefull for navigation between files)
   */
  public activeIndex: number;

  /**
   * current PDF to display
   */
  public pdfToDisplay: any;

  /**
   * current TXT/XML file to display
   */
  public txtToDisplay: string | ArrayBuffer;

  /**
   * if the current format is not displayable
   */
  public formatNotDisplayable: boolean;

  /**
   * the errorMessage in case the user is trying to display a non-supported file format
   */
  public errorMessage: string;
  /**
   * should use the native viewer or some exetrenal libs (native is not working with ie)
   */
  public msBrowsers: boolean;

  /**
   *
   * @param {TranslateService} translate - here for error Message translation
   * @param {FoldDetailService} foldDetailService - here to handle detail fold request
   * @param deviceService
   * @param httpService
   * @param fileSaverService
   */
  constructor(private translate: TranslateService, private foldDetailService: FoldDetailService,
              private deviceService: DeviceDetectorService, private httpService: HttpService,
              private fileSaverService: FileSaverService) {
  }

  /**
   * here to handle translation subscription
   */
  public ngOnInit(): void {
    this.deviceInfo = this.deviceService.getDeviceInfo();
    this.getTradForComponent();
    this.msBrowsers = (BROWSERS.IE === this.deviceInfo.browser || BROWSERS.MS_EDGE === this.deviceInfo.browser);
  }



  private getFileContentByPathFromProtectedUrl(fileName: string, filePath: string): void {
    this.httpService.$get(
      ApiConst.getArchContentByPath,
      undefined,
      { fileName, filePath }
    ).subscribe({
      next: (response) => {
        const blob = response.body;
        const blobUrl = URL.createObjectURL(blob);

        window.open(blobUrl, '_blank');

        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      },
      error: err => console.error('Erreur affichage PDF', err)
    });
  }


  /**
   * if the action column needs to be displayed
   * @return {boolean}
   */
  public thereIsAction(): boolean {
    return this.movable || this.consultable || this.downloadable || this.removable || this.convertibleAsText;
  }

  /**
   * download the PDF file corresponding to a row
   * @param {number} index - the row index
   */
  public downloadFile(index: number): void {
    let filename;
    let filepath;

    // Deposit case
    if (this.datas[index].sInternalName && this.datas[index].sInternalPath) {
      filename = this.datas[index].sInternalName;
      filepath = this.datas[index].sInternalPath;
    }

    // Treatment case
    else {
      filename = this.datas[index].sFileName;
      filepath = this.datas[index].sFilePath;
    }

    this.foldDetailService.downloadFileContent(filename, filepath, "ALL").subscribe(response => {
      const blob = new Blob([ response.body ], {type: response.headers.get('content-type')});
      this.fileSaverService.save(blob, this.datas[index].sFileName);
    });
  }

  /**
   * download the txt file corresponding to a row
   * @param {number} index - the row index
   */
  public downloadFileAsTXT(index: number): void {
    this.foldDetailService.getFileAsText(this.datas[index].sFileName, this.datas[index].sFilePath).subscribe(response => {
      const blob = new Blob([ response.body ], {type: response.headers.get('content-type')});
      this.fileSaverService.save(blob, this.datas[index].sFileName + '.txt');
    });
  }

  /**
   * display the selected file and remove (if exists) the previous from memory
   * @param {number} index - row index inside the p-table
   */
  public displayFile(index: number): void {
    let filename;
    let filepath;

    if (this.datas[index].sInternalName && this.datas[index].sInternalPath) {
      filename = this.datas[index].sInternalName;
      filepath = this.datas[index].sInternalPath;
    }

    // Treatment case
    else {
      filename = this.datas[index].sFileName;
      filepath = this.datas[index].sFilePath;
    }

    if (this.msBrowsers) {
      this.pdfToDisplay = ApiConst.apiUrl + '/getFileContentByPath/?filePath=' + filepath + '&fileName=' + filename ;
      this.getFileContentByPathFromProtectedUrl(filepath ,filename);
    }
    else {
      this.displayConsult = false;
      if (this.pdfToDisplay) {
        this.freeDocMemory(this.pdfToDisplay);
      }
      this.setDisplayableContentToNull();

      this.activeIndex = index;
      this.displayConsultName = this.datas[index].sFileNameIndice || this.datas[index].sFileName;

      this.displayRightArrow = index < this.datas.length - 1;
      this.displayLeftArrow = index > 0;

      // Deposit case

      this.foldDetailService.getFileContent(filename, filepath, "ALL").subscribe(response => {
        const typeOfReponse = response.headers.get('content-type');
        if (typeOfReponse && typeOfReponse.includes('pdf')) {
            this.displayPDF(response);
          }
          else if (typeOfReponse && typeOfReponse.includes('xml') || typeOfReponse.includes('text')) {
            this.displayTXT(response);
          }
          else {
            this.formatNotDisplayable = true;
          }
        }
      );
    }
  }

  /**
   * display a pdf file (without checking if it's one)
   * @param response - the response given by back-office
   */
  private displayPDF(response): void {
    const blob = new Blob([ response.body ], {type: response.headers.get('content-type')});
    this.pdfToDisplay = window.URL.createObjectURL(blob);
    if (this.msBrowsers) {
        this.pdfToDisplay = window.URL.createObjectURL(blob);
        window.open(this.pdfToDisplay, '_blank');
    }
    else {
      this.displayConsult = true;
    }
  }

  /**
   * display a txt file (without checking if it's one)
   * @param response - the response given by back-office
   */
  private displayTXT(response): void {
    if (this.msBrowsers) {
      const fileBlob = response.blob();
      const blob = new Blob([fileBlob], {type: response.headers.get('content-type')});
      this.pdfToDisplay = window.URL.createObjectURL(blob);
        this.pdfToDisplay = window.URL.createObjectURL(blob);
        window.open(this.pdfToDisplay, '_blank');
    }
    else {
      const reader = new FileReader();
      reader.readAsText(response.blob());
      reader.onloadend = () => {
        this.txtToDisplay = reader.result;
        this.displayConsult = true;
      };
    }
  }

  /**
   * free window URL (memory purposes)
   */
  public freeDocMemory(docToFree) {
    window.URL.revokeObjectURL(docToFree);
  }

  /**
   * reset every element that should be display to null
   */
  private setDisplayableContentToNull(): void {
    this.formatNotDisplayable = null;
    this.pdfToDisplay = null;
    this.txtToDisplay = null;
  }

  /**
   * obtain translation from TranslateService
   */
  private getTradForComponent(): void {
    this.translate.get('CUSTOM.LIST_COMPONENT.ERROR_DISPLAY_NOT_AVAILABLE').subscribe((error: string) => {
      this.errorMessage = error;
    });
  }

  public setWidth( col ): { width: string } {
    return { width: col.width };
  }
}
